import Collapse from '@mui/material/Collapse';
import { useState, useEffect, useCallback } from 'react';

import { usePathname } from 'src/routes/hooks';
import { useActiveLink } from 'src/routes/hooks/use-active-link';

import { NavListProps, NavSubListProps } from '../types';
import NavItem from './nav-item';

// ----------------------------------------------------------------------

export default function NavList({ data, depth, slotProps }: NavListProps) {
  const pathname = usePathname();

  const active = useActiveLink(data.path, !!data.children);

  const hasActiveExtraPath = useCallback(
    () => data.activePaths?.some((path) => pathname === path || pathname.startsWith(`${path}/`)) ?? false,
    [data.activePaths, pathname]
  );

  // Para itens com children, verifica se está na rota base ou em subrotas (criar/editar)
  const isActiveWithSubroutes = useCallback(() => {
    if (hasActiveExtraPath()) return true;

    if (!data.path || data.path === '#') return active;

    // Se não tem children, usa o active padrão
    if (!data.children) return active;

    // Se tem children, verifica se algum child tem o pathname como prefixo
    return data.children.some((child: any) => {
      if (!child.path || child.path === '#') return false;
      // Verifica se o pathname atual começa com o path do child
      return pathname.startsWith(child.path);
    });
  }, [data.path, data.children, pathname, active, hasActiveExtraPath]);

  const [openMenu, setOpenMenu] = useState(isActiveWithSubroutes());

  // Verifica se algum dos filhos está ativo
  const hasActiveChild = useCallback(() => {
    if (!data.children) return false;

    if (hasActiveExtraPath()) return true;

    return data.children.some((child: any) => {
      // Verifica se o path do child corresponde ao pathname atual
      if (child.path && child.path !== '#' && pathname.startsWith(child.path)) return true;
      if (child.children) {
        return child.children.some((subChild: any) => subChild.path && subChild.path !== '#' && pathname.startsWith(subChild.path));
      }
      return false;
    });
  }, [data.children, pathname, hasActiveExtraPath]);

  useEffect(() => {
    // Mantém o menu aberto se o item atual está ativo OU se algum filho está ativo
    if (isActiveWithSubroutes() || hasActiveChild()) {
      setOpenMenu(true);
    } else if (data.children) {
      // Fecha o menu se não está mais ativo e tem filhos
      setOpenMenu(false);
    }
  }, [pathname, isActiveWithSubroutes, hasActiveChild, data.children]);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      setOpenMenu((prev: any) => !prev);
    }
  }, [data.children]);

  // Para itens sem filhos, o open deve ser sempre false
  const shouldBeOpen = data.children ? openMenu : false;

  // Verifica se o item está ativo considerando subrotas (criar/editar)
  const isItemActive = useCallback(() => {
    if (hasActiveExtraPath()) return true;

    if (!data.path || data.path === '#' || data.path === '/') return active;

    // Verifica se o pathname é exatamente o path ou se é uma subrota (com / após o path)
    const isExactOrSubroute = pathname === data.path || pathname.startsWith(`${data.path}/`);

    // Não ativa se a rota contém categorias (para evitar que "Listar Estabelecimentos" seja marcado quando em categorias)
    if (pathname.includes('/categories')) {
      return pathname === data.path;
    }

    // Caso especial para notificações: marca "Gerenciar Notificações" como ativo para /notifications e /notifications/create
    if (
      data.path === '/dashboard/notifications/' &&
      (pathname === '/dashboard/notifications/' || pathname === '/dashboard/notifications/create')
    ) {
      return true;
    }

    return active || isExactOrSubroute;
  }, [active, pathname, data.path, hasActiveExtraPath]);

  const itemActive = isItemActive();

  return (
    <>
      <NavItem
        open={shouldBeOpen}
        onClick={handleToggleMenu}
        //
        title={data.title}
        path={data.path}
        icon={data.icon}
        info={data.info}
        roles={data.roles}
        caption={data.caption}
        disabled={data.disabled}
        favoriteId={data.favoriteId}
        isFavorite={data.favoriteId ? slotProps?.favoriteIds?.includes(data.favoriteId) : false}
        onToggleFavorite={slotProps?.onToggleFavorite}
        //
        depth={depth}
        hasChild={!!data.children}
        externalLink={data.path.includes('http')}
        currentRole={slotProps?.currentRole}
        //
        active={itemActive}
        className={itemActive ? 'active' : ''}
        sx={{
          mb: `${slotProps?.gap}px`,
          ...(depth === 1 ? slotProps?.rootItem : slotProps?.subItem),
        }}
      />

      {!!data.children && (
        <Collapse in={shouldBeOpen} unmountOnExit>
          <NavSubList data={data.children} depth={depth} slotProps={slotProps} />
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function NavSubList({ data, depth, slotProps }: NavSubListProps) {
  return (
    <>
      {data.map((list) => (
        <NavList key={list.title} data={list} depth={depth + 1} slotProps={slotProps} />
      ))}
    </>
  );
}
